import { supabase } from '@/lib/supabase'

export type OwnerContact = {
  userId: string | null
  name: string | null
  email: string | null
  avatar_url: string | null
  phone?: string | null
  country?: string | null
}

// Resolve, para um conjunto de contas, quem é o owner e quais são seus
// dados de contato (nome, e‑mail e avatar). Implementa os mesmos fallbacks
// usados nas telas de Clientes para manter SSOT/DRY entre módulos.
export async function getOwnerContactsByAccountIds(accountIds: string[]): Promise<Map<string, OwnerContact>> {
  const out = new Map<string, OwnerContact>()
  if (!accountIds?.length) return out

  // Accounts básicos (nome e primary_user)
  const { data: accounts } = await supabase
    .from('accounts')
    .select('id, name, primary_user_id')
    .in('id', accountIds)

  const ids = (accounts || []).map((a: any) => a.id)
  if (!ids.length) return out

  // Donos explícitos
  const { data: owners } = await supabase
    .from('account_users')
    .select('account_id, user_id, role')
    .in('account_id', ids)
    .eq('role', 'owner')

  // owner por conta, com fallback para primary_user_id
  const ownerByAcc = new Map<string, string | null>()
  for (const a of accounts || []) {
    const ow = (owners || []).find((o: any) => o.account_id === a.id)
    ownerByAcc.set(a.id, ow?.user_id || (a as any)?.primary_user_id || null)
  }

  const ownerUserIds = Array.from(new Set(Array.from(ownerByAcc.values()).filter(Boolean))) as string[]

  // Dados de contato (view master-segura)
  const { data: profs } = ownerUserIds.length
    ? await supabase.from('v_profiles_contact_master').select('id, full_name, email, avatar_url').in('id', ownerUserIds)
    : { data: [] as any[] }

  const profById = new Map((profs || []).map((p: any) => [p.id, p]))

  // Telefones e pas (para contato do owner)
  let phoneById = new Map<string, string | null>()
  let countryById = new Map<string, string | null>()
  if (ownerUserIds.length) {
    try {
      const { data: pfContact } = await supabase.from('profiles').select('id, phone, country').in('id', ownerUserIds)
      phoneById = new Map((pfContact || []).map((r: any) => [r.id, r.phone || null]))
      countryById = new Map((pfContact || []).map((r: any) => [r.id, (r.country || null) as string | null]))
    } catch {}
  }

  // Fallback de avatar: se a view não retornar avatar_url para todos, tenta profiles diretamente
  const needAvatarFallback = ownerUserIds.length > 0 && (profs || []).every((p: any) => !p?.avatar_url)
  if (needAvatarFallback) {
    try {
      const { data: pf } = await supabase.from('profiles').select('id, avatar_url').in('id', ownerUserIds)
      const map = new Map((pf || []).map((r: any) => [r.id, r.avatar_url || null]))
      for (const [id, p] of profById) {
        if (!p?.avatar_url && map.has(id)) (p as any).avatar_url = map.get(id)
      }
    } catch {}
  }

  // View específica por conta (inclui email direto de auth.users via join)
  // Usamos para suprir e-mails ausentes e, quando possível, o owner_id.
  let byAccountFromView = new Map<string, any>()
  try {
    const { data: v } = await supabase
      .from('v_accounts_owner_contact')
      .select('account_id, owner_user_id, full_name, email, avatar_url')
      .in('account_id', ids)
    byAccountFromView = new Map((v || []).map((r: any) => [r.account_id, r]))
  } catch {}

  // Monta saída com fallbacks coerentes com Customers
  for (const a of accounts || []) {
    const ownerId = ownerByAcc.get(a.id) || null
    const p: any = ownerId ? profById.get(ownerId) : null
    // Fallback: se name parecer email, usar como e‑mail; senão como nome
    const accName = (a as any)?.name || null
    const emailFallback = (() => {
      try {
        const nm = String(accName || '')
        return nm.includes('@') ? nm : null
      } catch { return null }
    })()
    const nameFromAccount = (() => {
      if (!accName) return null
      const nm = String(accName)
      if (nm.includes('@')) {
        try { return nm.split('@')[0] } catch { return nm }
      }
      return nm
    })()

    const row = byAccountFromView.get(a.id)
    const emailFromView = row?.email || null
    const ownerFromView = row?.owner_user_id || null
    const avatarFromView = row?.avatar_url || null
    out.set(a.id, {
      userId: ownerId || ownerFromView,
      name: (p?.full_name ?? row?.full_name ?? nameFromAccount ?? null) as string | null,
      email: (p?.email ?? emailFromView ?? emailFallback ?? null) as string | null,
      avatar_url: (p?.avatar_url ?? avatarFromView ?? null) as string | null,
      phone: (ownerId ? (phoneById.get(ownerId) ?? null) : null),
      country: (ownerId ? (countryById.get(ownerId) ?? null) : null),
    })
  }

  return out
}
